import { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { CALC_QUESTIONS, REGIONS } from '../../data/actions';

function ComparisonBar({ label, value, color, maxValue }) {
  return (
    <div style={{ marginBottom: "0.6rem" }}>
      <div 
        style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          fontSize: "0.73rem", 
          color: "#9ca3af", 
          marginBottom: "0.22rem" 
        }}
      >
        <span>{label}</span>
        <span style={{ color }}>{value}t CO₂</span>
      </div>
      <div 
        style={{ 
          background: "rgba(255,255,255,0.06)", 
          borderRadius: "99px", 
          height: "7px", 
          overflow: "hidden" 
        }}
      >
        <div 
          style={{ 
            width: `${Math.min(100, (value / maxValue) * 100)}%`, 
            height: "100%", 
            background: color, 
            borderRadius: "99px", 
            transition: "width 0.6s ease" 
          }} 
        />
      </div>
    </div>
  );
}

function BreakdownItem({ question, value }) {
  const maxValue = Math.max(...question.opts.map(o => o.v));
  
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.7rem", marginBottom: "0.55rem" }}>
      <span style={{ width: "1.4rem", fontSize: "1rem", textAlign: "center" }} aria-hidden="true">
        {question.icon}
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: "0.7rem", color: "#9ca3af", marginBottom: "0.18rem" }}>
          {question.label}
        </div>
        <div 
          style={{ 
            background: "rgba(255,255,255,0.06)", 
            borderRadius: "99px", 
            height: "5px", 
            overflow: "hidden" 
          }}
        >
          <div 
            style={{ 
              width: `${(value / maxValue) * 100}%`, 
              height: "100%", 
              background: value > 2 ? "#ef4444" : value > 1 ? "#f97316" : "#4ade80", 
              borderRadius: "99px" 
            }} 
          />
        </div>
      </div>
      <span style={{ fontSize: "0.7rem", color: "#6b7280", minWidth: "32px", textAlign: "right" }}>
        {value}t
      </span>
    </div>
  );
}

export function Calculator() {
  const {
    calculatorAnswers, calculatorStep, calculatorComplete,
    footprint, footprintLabel, footprintHistory,
    items, potentialSavings, region, setRegion,
    season, setSeason,
    userGoal, setUserGoal,
    goalTimeline, setGoalTimeline,
    goalReduction, goalProgress, monthlyReduction,
    handleCalculatorAnswer, resetCalculator, setView, setCalculatorStep
  } = useApp();

  const selectedRegion = REGIONS.find(r => r.id === region) || REGIONS[0];
  const [showGoalEditor, setShowGoalEditor] = useState(false);
  const [goalInput, setGoalInput] = useState(userGoal?.toString() || "");
  
  // Pie chart data
  const breakdownData = CALC_QUESTIONS.map(q => ({
    label: q.label,
    icon: q.icon,
    value: calculatorAnswers[q.id] || 0,
    color: q.id === "transport" ? "#ef4444" : 
           q.id === "diet" ? "#f97316" : 
           q.id === "flights" ? "#eab308" : 
           q.id === "energy" ? "#22c55e" : 
           q.id === "shopping" ? "#3b82f6" : 
           q.id === "waste" ? "#8b5cf6" : "#06b6d4"
  })).filter(d => d.value > 0);
  
  const totalBreakdown = breakdownData.reduce((sum, d) => sum + d.value, 0);

  const handleSetGoal = () => {
    const goal = parseFloat(goalInput);
    if (!isNaN(goal) && goal >= 0) {
      setUserGoal(goal);
      setShowGoalEditor(false);
    }
  };

  return (
    <div style={{ animation: "slideIn 0.3s ease" }}>
      <h2 style={{ fontSize: "1.6rem", fontWeight: "normal", color: "#f0fdf4", marginBottom: "0.3rem" }}>
        Carbon <em style={{ color: "#c4b5fd" }}>Footprint Calculator</em>
      </h2>
      <p style={{ color: "var(--text-muted)", fontSize: "0.83rem", marginBottom: "1.5rem" }}>
        7 quick questions to estimate your annual CO₂ and see where to focus.
      </p>

      {/* Region and Season selectors */}
      <div style={{ marginBottom: "1.25rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: "200px" }}>
          <label 
            htmlFor="region-select"
            style={{ 
              fontSize: "0.68rem", 
              letterSpacing: "0.15em", 
              color: "var(--text-muted)", 
              textTransform: "uppercase", 
              marginBottom: "0.4rem",
              display: "block"
            }}
          >
            🌍 Your Region
          </label>
          <select
            id="region-select"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            style={{
              background: "var(--bg-tertiary)",
              border: "1px solid var(--border-color)",
              color: "var(--text-primary)",
              padding: "0.5rem 0.9rem",
              borderRadius: "8px",
              fontSize: "0.82rem",
              cursor: "pointer",
              width: "100%",
            }}
          >
            {REGIONS.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: "200px" }}>
          <label 
            htmlFor="season-select"
            style={{ 
              fontSize: "0.68rem", 
              letterSpacing: "0.15em", 
              color: "var(--text-muted)", 
              textTransform: "uppercase", 
              marginBottom: "0.4rem",
              display: "block"
            }}
          >
            ☀️ Current Season
          </label>
          <select
            id="season-select"
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            style={{
              background: "var(--bg-tertiary)",
              border: "1px solid var(--border-color)",
              color: "var(--text-primary)",
              padding: "0.5rem 0.9rem",
              borderRadius: "8px",
              fontSize: "0.82rem",
              cursor: "pointer",
              width: "100%",
            }}
          >
            <option value="yearRound">Year-round</option>
            <option value="summer">Summer (AC/Travel)</option>
            <option value="winter">Winter (Heating)</option>
          </select>
        </div>
      </div>

      {/* Step dots */}
      <div style={{ display: "flex", gap: "0.35rem", marginBottom: "1.5rem" }}>
        {CALC_QUESTIONS.map((q, i) => (
          <div
            key={q.id}
            style={{
              flex: 1,
              height: "4px",
              borderRadius: "99px",
              background: calculatorAnswers[q.id] !== undefined 
                ? "#a855f7" 
                : i === calculatorStep && !calculatorComplete 
                  ? "rgba(168,85,247,0.35)" 
                  : "var(--border-color)",
              transition: "background 0.3s",
            }}
          />
        ))}
      </div>

      {!calculatorComplete ? (
        <div>
          <div 
            style={{ 
              fontSize: "0.68rem", 
              letterSpacing: "0.2em", 
              color: "#a78bfa", 
              textTransform: "uppercase", 
              marginBottom: "0.4rem" 
            }}
          >
            {CALC_QUESTIONS[calculatorStep].icon} {CALC_QUESTIONS[calculatorStep].label} · {calculatorStep + 1} of {CALC_QUESTIONS.length}
          </div>
          <div 
            style={{ 
              fontSize: "1.2rem", 
              color: "#f0fdf4", 
              marginBottom: "1.1rem", 
              fontStyle: "italic" 
            }}
          >
            {CALC_QUESTIONS[calculatorStep].q}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
            {CALC_QUESTIONS[calculatorStep].opts.map(opt => (
              <button
                key={opt.l}
                onClick={() => handleCalculatorAnswer(CALC_QUESTIONS[calculatorStep].id, opt.v)}
                style={{
                  background: calculatorAnswers[CALC_QUESTIONS[calculatorStep].id] === opt.v 
                    ? "rgba(168,85,247,0.18)" 
                    : "rgba(168,85,247,0.07)",
                  border: `1px solid ${calculatorAnswers[CALC_QUESTIONS[calculatorStep].id] === opt.v 
                    ? "rgba(168,85,247,0.5)" 
                    : "rgba(168,85,247,0.2)"}`,
                  color: "#e8f5e9",
                  padding: "0.82rem 1rem",
                  borderRadius: "10px",
                  textAlign: "left",
                  fontSize: "0.88rem",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                {opt.l}
              </button>
            ))}
          </div>
          {calculatorStep > 0 && (
            <button
              onClick={() => setCalculatorStep(s => s - 1)}
              style={{
                background: "none",
                border: "none",
                color: "#6b7280",
                marginTop: "0.85rem",
                fontSize: "0.78rem",
                cursor: "pointer",
              }}
            >
              ← Back
            </button>
          )}
        </div>
      ) : (
        <div>
          {/* Result hero */}
          <div 
            style={{ 
              textAlign: "center", 
              background: "rgba(168,85,247,0.07)", 
              border: "1px solid rgba(168,85,247,0.2)", 
              borderRadius: "16px", 
              padding: "1.5rem", 
              marginBottom: "1.25rem" 
            }}
          >
            <div 
              style={{ 
                fontSize: "0.68rem", 
                letterSpacing: "0.22em", 
                color: "#a78bfa", 
                textTransform: "uppercase", 
                marginBottom: "0.5rem" 
              }}
            >
              Your Estimated Annual Footprint
            </div>
            <div 
              style={{ 
                fontSize: "3.5rem", 
                fontFamily: "monospace", 
                fontWeight: "bold", 
                color: "#c4b5fd" 
              }}
            >
              {footprint.toFixed(1)}t
            </div>
            <div style={{ fontSize: "0.82rem", color: "#9ca3af", marginTop: "0.25rem" }}>
              CO₂ equivalent · {footprintLabel}
            </div>
          </div>

          {/* Comparison */}
          <div style={{ marginBottom: "1.25rem" }}>
            <div 
              style={{ 
                fontSize: "0.68rem", 
                letterSpacing: "0.15em", 
                color: "#6b7280", 
                textTransform: "uppercase", 
                marginBottom: "0.65rem" 
              }}
            >
              How you compare
            </div>
            {[
              { label: "You", value: footprint, color: "#c4b5fd" },
              { label: `${selectedRegion.name} Average`, value: selectedRegion.avgFootprint, color: "#f97316" },
              { label: "World Average", value: 7, color: "#fbbf24" },
              { label: "1.5°C Target", value: 2.5, color: "#4ade80" },
            ].map(bar => (
              <ComparisonBar
                key={bar.label}
                label={bar.label}
                value={bar.value}
                color={bar.color}
                maxValue={Math.max(footprint, selectedRegion.avgFootprint, 11)}
              />
            ))}
          </div>

          {/* What-If Simulator */}
          <div 
            style={{ 
              marginBottom: "1.25rem", 
              background: "rgba(34,197,94,0.05)", 
              border: "1px solid rgba(34,197,94,0.15)", 
              borderRadius: "14px", 
              padding: "1rem 1.1rem" 
            }}
          >
            <div 
              style={{ 
                fontSize: "0.68rem", 
                letterSpacing: "0.15em", 
                color: "#4ade80", 
                textTransform: "uppercase", 
                marginBottom: "0.65rem" 
              }}
            >
              🔮 What-if simulator
            </div>
            <p style={{ color: "#9ca3af", fontSize: "0.8rem", lineHeight: 1.6, margin: "0 0 0.85rem" }}>
              If you commit to all remaining {items.filter(a => !a.done).length} actions, your projected footprint could drop by up to{" "}
              <strong style={{ color: "#4ade80" }}>{(potentialSavings / 1000).toFixed(1)}t</strong> — from{" "}
              <strong style={{ color: "#c4b5fd" }}>{footprint.toFixed(1)}t</strong> down to approximately{" "}
              <strong style={{ color: "#4ade80" }}>{Math.max(0, footprint - potentialSavings / 1000).toFixed(1)}t</strong> CO₂/yr.
            </p>
            <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "0.65rem", color: "#6b7280", marginBottom: "0.2rem" }}>Current</div>
                <div 
                  style={{ 
                    background: "rgba(255,255,255,0.06)", 
                    borderRadius: "99px", 
                    height: "7px", 
                    overflow: "hidden" 
                  }}
                >
                  <div 
                    style={{ 
                      width: `${Math.min(100, (footprint / Math.max(footprint, 11)) * 100)}%`, 
                      height: "100%", 
                      background: "#c4b5fd", 
                      borderRadius: "99px" 
                    }} 
                  />
                </div>
              </div>
              <span style={{ color: "#4ade80", fontSize: "0.8rem" }}>→</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "0.65rem", color: "#6b7280", marginBottom: "0.2rem" }}>After all actions</div>
                <div 
                  style={{ 
                    background: "rgba(255,255,255,0.06)", 
                    borderRadius: "99px", 
                    height: "7px", 
                    overflow: "hidden" 
                  }}
                >
                  <div 
                    style={{ 
                      width: `${Math.min(100, (Math.max(0, footprint - potentialSavings / 1000) / Math.max(footprint, 11)) * 100)}%`, 
                      height: "100%", 
                      background: "#4ade80", 
                      borderRadius: "99px", 
                      transition: "width 0.6s" 
                    }} 
                  />
                </div>
              </div>
            </div>
            <button
              onClick={() => setView("actions")}
              style={{
                marginTop: "0.85rem",
                background: "rgba(34,197,94,0.12)",
                border: "1px solid rgba(34,197,94,0.25)",
                color: "#86efac",
                padding: "0.42rem 0.9rem",
                borderRadius: "8px",
                fontSize: "0.78rem",
                cursor: "pointer",
              }}
            >
              View actions →
            </button>
          </div>

          {/* Pie Chart Breakdown */}
          <div style={{ marginBottom: "1.25rem" }}>
            <div 
              style={{ 
                fontSize: "0.68rem", 
                letterSpacing: "0.15em", 
                color: "var(--text-muted)", 
                textTransform: "uppercase", 
                marginBottom: "0.65rem" 
              }}
            >
              📊 Emissions breakdown
            </div>
            {/* Pie chart visualization */}
            <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", marginBottom: "1rem" }}>
              <div style={{ position: "relative", width: "100px", height: "100px" }}>
                <svg viewBox="0 0 100 100" style={{ transform: "rotate(-90deg)" }}>
                  {breakdownData.map((item, i) => {
                    const prevPercent = breakdownData.slice(0, i).reduce((sum, d) => sum + (d.value / totalBreakdown) * 100, 0);
                    const percent = (item.value / totalBreakdown) * 100;
                    const dashArray = `${percent} ${100 - percent}`;
                    const dashOffset = -prevPercent;
                    return (
                      <circle
                        key={item.label}
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke={item.color}
                        strokeWidth="20"
                        strokeDasharray={dashArray}
                        strokeDashoffset={dashOffset}
                      />
                    );
                  })}
                </svg>
                <div style={{ 
                  position: "absolute", 
                  top: "50%", 
                  left: "50%", 
                  transform: "translate(-50%, -50%)",
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  color: "var(--text-primary)"
                }}>
                  {footprint.toFixed(1)}t
                </div>
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                {breakdownData.slice(0, 5).map(item => (
                  <div key={item.label} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <div style={{ width: "10px", height: "10px", borderRadius: "2px", background: item.color }} />
                    <span style={{ fontSize: "0.7rem", color: "var(--text-secondary)", flex: 1 }}>{item.icon} {item.label}</span>
                    <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
                      {totalBreakdown > 0 ? Math.round((item.value / totalBreakdown) * 100) : 0}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
            {/* List breakdown */}
            {CALC_QUESTIONS.map(q => (
              <BreakdownItem
                key={q.id}
                question={q}
                value={calculatorAnswers[q.id] || 0}
              />
            ))}
          </div>

          {/* Goal Setting */}
          <div 
            style={{ 
              marginBottom: "1.25rem", 
              background: "rgba(34,197,94,0.05)", 
              border: "1px solid rgba(34,197,94,0.15)", 
              borderRadius: "14px", 
              padding: "1rem 1.1rem" 
            }}
          >
            <div 
              style={{ 
                fontSize: "0.68rem", 
                letterSpacing: "0.15em", 
                color: "var(--accent)", 
                textTransform: "uppercase", 
                marginBottom: "0.65rem" 
              }}
            >
              🎯 Carbon reduction goal
            </div>
            {userGoal ? (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                  <div>
                    <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                      Target: <strong style={{ color: "var(--accent)" }}>{userGoal}t</strong> CO₂/year
                    </div>
                    <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
                      Timeline: {goalTimeline === "6months" ? "6 months" : goalTimeline === "1year" ? "1 year" : "2 years"}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowGoalEditor(true)}
                    style={{
                      background: "var(--bg-tertiary)",
                      border: "1px solid var(--border-color)",
                      color: "var(--text-secondary)",
                      padding: "0.3rem 0.6rem",
                      borderRadius: "6px",
                      fontSize: "0.7rem",
                      cursor: "pointer",
                    }}
                  >
                    Edit
                  </button>
                </div>
                {/* Goal progress */}
                <div style={{ marginBottom: "0.5rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.65rem", color: "var(--text-muted)", marginBottom: "0.25rem" }}>
                    <span>Progress</span>
                    <span>{Math.round(goalProgress)}%</span>
                  </div>
                  <div style={{ background: "var(--bg-tertiary)", borderRadius: "99px", height: "6px", overflow: "hidden" }}>
                    <div style={{ width: `${goalProgress}%`, height: "100%", background: "var(--accent)", borderRadius: "99px", transition: "width 0.5s" }} />
                  </div>
                </div>
                <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", lineHeight: 1.5 }}>
                  {goalReduction > 0 ? (
                    <>Reduce by <strong style={{ color: "var(--accent)" }}>{goalReduction.toFixed(1)}t</strong> more to reach your goal
                    {monthlyReduction > 0 && <>, about <strong>{monthlyReduction.toFixed(2)}t/month</strong></>}</>
                  ) : (
                    <span style={{ color: "var(--accent)" }}>🎉 You've reached your goal!</span>
                  )}
                </div>
              </div>
            ) : showGoalEditor ? (
              <div>
                <div style={{ marginBottom: "0.75rem" }}>
                  <label style={{ fontSize: "0.7rem", color: "var(--text-secondary)", display: "block", marginBottom: "0.3rem" }}>
                    Target footprint (tons CO₂/year)
                  </label>
                  <input
                    type="number"
                    value={goalInput}
                    onChange={(e) => setGoalInput(e.target.value)}
                    placeholder="e.g., 5"
                    style={{
                      background: "var(--bg-tertiary)",
                      border: "1px solid var(--border-color)",
                      color: "var(--text-primary)",
                      padding: "0.5rem 0.75rem",
                      borderRadius: "8px",
                      fontSize: "0.85rem",
                      width: "100%",
                      maxWidth: "150px",
                    }}
                  />
                </div>
                <div style={{ marginBottom: "0.75rem" }}>
                  <label style={{ fontSize: "0.7rem", color: "var(--text-secondary)", display: "block", marginBottom: "0.3rem" }}>
                    Timeline
                  </label>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    {["6months", "1year", "2years"].map(t => (
                      <button
                        key={t}
                        onClick={() => setGoalTimeline(t)}
                        style={{
                          flex: 1,
                          background: goalTimeline === t ? "var(--accent)" : "var(--bg-tertiary)",
                          color: goalTimeline === t ? "#fff" : "var(--text-secondary)",
                          border: `1px solid ${goalTimeline === t ? "var(--accent)" : "var(--border-color)"}`,
                          padding: "0.4rem 0.5rem",
                          borderRadius: "6px",
                          fontSize: "0.7rem",
                          cursor: "pointer",
                        }}
                      >
                        {t === "6months" ? "6 mo" : t === "1year" ? "1 year" : "2 years"}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button
                    onClick={handleSetGoal}
                    style={{
                      flex: 1,
                      background: "var(--accent)",
                      color: "#fff",
                      padding: "0.5rem",
                      borderRadius: "8px",
                      fontSize: "0.8rem",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Set Goal
                  </button>
                  <button
                    onClick={() => setShowGoalEditor(false)}
                    style={{
                      background: "var(--bg-tertiary)",
                      border: "1px solid var(--border-color)",
                      color: "var(--text-secondary)",
                      padding: "0.5rem 1rem",
                      borderRadius: "8px",
                      fontSize: "0.8rem",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowGoalEditor(true)}
                style={{
                  background: "var(--bg-tertiary)",
                  border: "1px solid var(--border-color)",
                  color: "var(--text-secondary)",
                  padding: "0.6rem 1rem",
                  borderRadius: "8px",
                  fontSize: "0.8rem",
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                + Set a reduction goal →
              </button>
            )}
          </div>

          {/* Footprint History */}
          {footprintHistory.length > 1 && (
            <div style={{ marginBottom: "1.25rem" }}>
              <div 
                style={{ 
                  fontSize: "0.68rem", 
                  letterSpacing: "0.15em", 
                  color: "#6b7280", 
                  textTransform: "uppercase", 
                  marginBottom: "0.65rem" 
                }}
              >
                📈 Your footprint history
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                {footprintHistory.map((h, i) => {
                  const maxFp = Math.max(...footprintHistory.map(x => x.fp), 1);
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
                      <span style={{ fontSize: "0.68rem", color: "#6b7280", minWidth: "80px" }}>{h.date}</span>
                      <div 
                        style={{ 
                          flex: 1, 
                          background: "rgba(255,255,255,0.06)", 
                          borderRadius: "99px", 
                          height: "5px", 
                          overflow: "hidden" 
                        }}
                      >
                        <div 
                          style={{ 
                            width: `${(h.fp / maxFp) * 100}%`, 
                            height: "100%", 
                            background: i === 0 ? "#c4b5fd" : "rgba(196,181,253,0.35)", 
                            borderRadius: "99px", 
                            transition: "width 0.5s" 
                          }} 
                        />
                      </div>
                      <span 
                        style={{ 
                          fontSize: "0.7rem", 
                          color: i === 0 ? "#c4b5fd" : "#6b7280", 
                          minWidth: "30px", 
                          textAlign: "right" 
                        }}
                      >
                        {h.fp}t
                      </span>
                      {i > 0 && footprintHistory[i - 1] && (
                        <span 
                          style={{ 
                            fontSize: "0.62rem", 
                            color: h.fp < footprintHistory[i - 1].fp ? "#4ade80" : "#f87171", 
                            minWidth: "30px" 
                          }}
                        >
                          {h.fp < footprintHistory[i - 1].fp 
                            ? `▼${(footprintHistory[i - 1].fp - h.fp).toFixed(1)}` 
                            : `▲${(h.fp - footprintHistory[i - 1].fp).toFixed(1)}`}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: "0.65rem" }}>
            <button
              onClick={resetCalculator}
              style={{
                flex: 1,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#9ca3af",
                padding: "0.7rem",
                borderRadius: "10px",
                fontSize: "0.8rem",
                cursor: "pointer",
              }}
            >
              Recalculate
            </button>
            <button
              onClick={() => setView("actions")}
              style={{
                flex: 2,
                background: "rgba(34,197,94,0.14)",
                border: "1px solid rgba(34,197,94,0.28)",
                color: "#86efac",
                padding: "0.7rem",
                borderRadius: "10px",
                fontSize: "0.8rem",
                cursor: "pointer",
              }}
            >
              View my action plan →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
